import styled from 'styled-components'
import { useEffect, useMemo, useState, useRef } from 'react'
import { useSelector } from 'react-redux'
import { fireUIRenderEvent } from 'src/modules/analytics/v3'
import { selectStateForProtobuf } from 'src/modules/reducers'
import { selectAnalyticsReady } from 'src/modules/reducers/analytics'
import { useMultipleChannelSelectors } from 'src/modules/hooks/useChannelSelector'
import { useMarketCtaTexts } from 'src/modules/hooks/useCtaText'
import {
  body,
  colors,
  maxWidth,
  mediaQuery,
  spacing,
  typographyMixin,
} from 'src/constants/marketing-ds'
import PlanCardWithImage from 'src/components/common/PlanCardWithImage'
import { SectionContainer, SectionHeader } from 'src/components/common/SharedComponents'

export const PlanCards = ({
  title,
  plans = [],
  sharedData = {},
  lang = 'en',
  showLengthText = true,
  footnote,
}) => {
  const planCardsTitle =
    title ||
    (lang === 'es'
      ? plans.length > 1
        ? 'Elige el plan que más te interesa'
        : null
      : plans.length > 1
        ? 'Which plan is right for you?'
        : null)
  const stateForProtobuf = useSelector(selectStateForProtobuf)
  const [bannerHeight, setBannerHeight] = useState(0)
  const analyticsReady = useSelector(selectAnalyticsReady)
  const sectionContentRef = useRef(null)
  const isEventSent = useRef(false)

  // Check if any plans have banners (text or image)
  const hasBanners = useMemo(() => {
    return plans.some((plan) => plan?.banner || plan?.bannerImage)
  }, [plans])

  const channelTypes = useMemo(() => {
    return plans.map((plan) => plan?.channelId)
  }, [plans])
  const channelList = useMultipleChannelSelectors(channelTypes)

  const defaultMarketCtaTexts = useMarketCtaTexts({ lang })

  // Measure banner heights after render
  useEffect(() => {
    if (!hasBanners) {
      setBannerHeight(0)
      return
    }

    const measureBannerHeights = () => {
      if (!sectionContentRef.current) return

      const banners = sectionContentRef.current.querySelectorAll('.planCard__banner')
      let maxHeight = 0

      banners.forEach((banner) => {
        const height = banner.getBoundingClientRect().height
        maxHeight = Math.max(maxHeight, height)
      })

      setBannerHeight(maxHeight)
    }

    const timeoutId = setTimeout(measureBannerHeights, 100)
    window.addEventListener('resize', measureBannerHeights)

    return () => {
      clearTimeout(timeoutId)
      window.removeEventListener('resize', measureBannerHeights)
    }
  }, [hasBanners, plans])

  useEffect(() => {
    if (!isEventSent.current && plans.length > 0 && analyticsReady) {
      fireUIRenderEvent(
        {
          element: plans.map((p) => p.plan).join(', '),
          component: 'plan_cards',
        },
        stateForProtobuf
      )
      isEventSent.current = true
    }
  }, [plans, analyticsReady])

  if (plans.length === 0) return null

  return (
    <SectionContainer backgroundColor={colors.gradients.slate.light}>
      <StyledBundleSection id="select-plan">
        {planCardsTitle && <SectionHeader title={planCardsTitle} variant="light" />}
        <div>
          <StyledSectionContent ref={sectionContentRef} bannerHeight={bannerHeight}>
            {plans.map((plan, index) => (
              <PlanCardWithImage
                lang={lang}
                titleLogo={plan.titleLogo}
                key={plan.title || plan.plan || index}
                promoImg={plan.promoImg}
                plan={plan.plan}
                title={plan.title}
                subtitle={plan.subtitle}
                price={plan.price % 1 === 0 ? plan.price : Math.round(plan.price * 1e2) / 1e2}
                discountedPrice={
                  plan.discountedPrice % 1 === 0
                    ? plan.discountedPrice
                    : Math.round(plan.discountedPrice * 1e2) / 1e2
                }
                savingsNote={plan.savingsNote}
                listItems={plan.listItems({
                  teams: plan.teams,
                  rsn: plan.rsn,
                  channels: channelList[plan.channelId],
                  ...sharedData,
                })}
                offerCode={plan.offerCode}
                bannerBgColor={plan.bannerBgColor}
                asterisk={plan.asterisk}
                footnote={
                  typeof plan?.footnote === 'function'
                    ? plan.footnote({
                        price: plan.price,
                        discountedPrice: plan.discountedPrice,
                        rsnName: plan.rsnName,
                      })
                    : plan?.footnote
                }
                networkLogos={plan.networkLogos}
                ctaText={plan?.ctaText || defaultMarketCtaTexts[plan.market || 'us']}
                cardClassName={
                  plan.plan +
                  (plan.banner || plan.bannerImage ? ' planCard-container-with-banner' : '')
                }
                banner={plan.banner}
                bannerImage={plan.bannerImage}
                backgroundColor={plan.backgroundColor}
                imageOverlay={plan.imageOverlay}
                fillImageSpace={plan.fillImageSpace}
                footnoteInCard={
                  typeof plan?.footnoteInCard === 'function'
                    ? plan.footnoteInCard({
                        price: plan.price,
                        discountedPrice: plan.discountedPrice,
                        rsnName: plan.rsnName,
                      })
                    : plan?.footnoteInCard
                }
                showLengthText={showLengthText}
              />
            ))}
          </StyledSectionContent>
          {footnote && <div className="section-footnote">{footnote}</div>}
        </div>
      </StyledBundleSection>
    </SectionContainer>
  )
}

const StyledBundleSection = styled.section`
  color: #fff;
  padding: ${spacing.xl} 0;
  margin: 0;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center;
  gap: ${spacing.lg};

  ${mediaQuery.tablet} {
    padding: ${spacing.xxxl} 0;
    gap: ${spacing.xl};
  }
  ${mediaQuery.desktop} {
    gap: ${spacing.xxl};
  }

  #select-plan {
    position: absolute;
    top: -80px;
  }

  .section-footnote {
    ${typographyMixin(body.xs)}
    text-align: center;
    color: ${colors.white};
    min-height: 60px;
    margin: ${spacing.sm} auto 0;
    padding: 0 ${spacing.sm};
    max-width: ${maxWidth.container};
  }
`

const StyledSectionContent = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
  max-width: ${maxWidth.desktop};
  gap: ${spacing.xl};
  ${mediaQuery.desktop} {
    align-items: stretch;
    flex-direction: row;
    gap: ${spacing.md};
    align-items: flex-start;
  }

  /* Dynamic top padding based on banner height */
  ${(props) =>
    props.bannerHeight &&
    props.bannerHeight > 0 &&
    `
    padding-top: ${props.bannerHeight}px;
    .planCard-container-with-banner:not(:first-child) {
      margin-top: ${props.bannerHeight}px;
      ${mediaQuery.desktop} {
        margin-top: 0;
      }
    }
  `}
`
